<div class="flex h-full w-full flex-col overflow-hidden bg-white">
  <!-- Mode indicator -->
  @if (mode() !== 'view') {
    <div class="flex items-center gap-2 border-b border-amber-100 bg-amber-50/70 px-3 py-1 text-[11px] text-amber-700">
      <dojo-icon icon="info-circle" [size]="12" />
      <span>{{ getModeLabel() }}</span>
      @if (mode() === 'connect-to' && connectFromAnchor()) {
        <span class="rounded bg-blue-500 px-1.5 py-px text-[10px] font-medium text-white">
          From: {{ connectFromAnchor() }}
        </span>
      }
    </div>
  }

  <!-- Main content area -->
  <div class="flex flex-1 overflow-hidden">
    <!-- LaTeX Preview Panel -->
    <div
      class="latex-preview relative flex flex-1 items-center justify-center overflow-auto p-8"
      #latexContainer
      [class.cursor-crosshair]="mode() !== 'view'"
      [class.select-none]="mode() !== 'view'"
      (mousedown)="onLatexMouseDown($event)"
      (mousemove)="onLatexMouseMove($event); onLatexMouseMoveLasso($event)"
      (mouseup)="onLatexMouseUp($event)"
      (mouseleave)="onLatexMouseUp($event)"
      (click)="onLatexClick($event)"
      (wheel)="onLatexWheel($event)"
    >
      <dojo-super-latex [content]="localContent()" />

      <!-- Hover highlight overlay for existing anchors -->
      @if (hoveredAnchor(); as hovered) {
        <div
          class="pointer-events-none absolute z-10 rounded border-2 border-blue-500 bg-blue-500/20"
          [style.left.px]="getRelativeLeft(hovered.rect)"
          [style.top.px]="getRelativeTop(hovered.rect)"
          [style.width.px]="hovered.rect.width"
          [style.height.px]="hovered.rect.height"
        >
          <span
            class="absolute -top-6 left-0 whitespace-nowrap rounded bg-blue-500 px-1.5 py-0.5 text-[10px] text-white"
          >
            {{ hovered.id }}
          </span>
        </div>
      }

      <!-- Hover highlight for create-anchor mode (non-anchor elements) -->
      @if (mode() === 'create-anchor' && hoveredElement(); as hovered) {
        @if (!hovered.isExistingAnchor) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-dashed border-emerald-500 bg-emerald-500/20"
            [style.left.px]="getRelativeLeft(hovered.rect)"
            [style.top.px]="getRelativeTop(hovered.rect)"
            [style.width.px]="hovered.rect.width"
            [style.height.px]="hovered.rect.height"
          >
            <span
              class="absolute -top-6 left-0 flex items-center gap-1.5 whitespace-nowrap rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] text-white"
            >
              "{{ hovered.text }}"
              @if (hoverableElements().length > 1) {
                <span class="opacity-75">(scroll: {{ hoverLevel() + 1 }}/{{ hoverableElements().length }})</span>
              }
            </span>
          </div>
        }
      }

      <!-- Selected anchors overlay -->
      @for (anchorId of selectedAnchors(); track anchorId) {
        @if (getAnchorRect(anchorId); as rect) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-emerald-500 bg-emerald-500/20"
            [style.left.px]="getRelativeLeft(rect)"
            [style.top.px]="getRelativeTop(rect)"
            [style.width.px]="rect.width"
            [style.height.px]="rect.height"
          ></div>
        }
      }

      <!-- Lasso selection rectangle -->
      @if (getLassoRect(); as rect) {
        <div
          class="pointer-events-none absolute z-20 border-2 border-dashed border-violet-500 bg-violet-500/10"
          [style.left.px]="rect.left"
          [style.top.px]="rect.top"
          [style.width.px]="rect.width"
          [style.height.px]="rect.height"
        ></div>
      }

      <!-- Lasso selection highlight -->
      @if (lassoSelection(); as selection) {
        <div
          class="pointer-events-none absolute z-10 rounded border-2 border-violet-500 bg-violet-500/20"
          [style.left.px]="getRelativeLeft(selection.combinedRect)"
          [style.top.px]="getRelativeTop(selection.combinedRect)"
          [style.width.px]="selection.combinedRect.width"
          [style.height.px]="selection.combinedRect.height"
        >
          <span
            class="absolute -top-6 left-0 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap rounded bg-violet-500 px-1.5 py-0.5 text-[10px] text-white"
          >
            {{ selection.texts.join('') }}
          </span>
        </div>
      }
    </div>

    <!-- Side Panel -->
    <div class="flex w-[360px] shrink-0 flex-col border-l border-neutral-200 bg-white">
      <div class="flex-1 overflow-y-auto">
        <!-- Layout Settings -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-600">Layout</span>
          <div class="mt-2.5 space-y-2.5">
            <div class="toggle-row flex items-center justify-between">
              <span class="text-xs text-neutral-600">Gray Background Highlight</span>
              <ion-toggle [checked]="localHighlight()" (ionChange)="toggleHighlight($any($event).detail.checked)" />
            </div>
            <dojo-single-select
              [formCtrl]="alignmentControl"
              [options]="alignmentOptions"
              [optionValue]="'value'"
              [optionLabel]="'label'"
              [compact]="true"
              [size]="'m'"
              [label]="'Alignment'"
            />
          </div>
        </div>

        <!-- Anchors List -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-600">
            Anchors
            <span class="normal-case tracking-normal">({{ existingAnchors().length }})</span>
          </span>

          @if (existingAnchors().length === 0) {
            <p class="py-3 text-center text-xs text-neutral-400">No anchors yet</p>
          } @else {
            <mat-accordion class="anchor-accordion mt-2" displayMode="flat" multi>
              @for (anchor of existingAnchors(); track anchor.id) {
                <mat-expansion-panel
                  class="anchor-panel"
                  (opened)="onAnchorPanelOpened(anchor.id)"
                  (closed)="onAnchorPanelClosed(anchor.id)"
                >
                  <mat-expansion-panel-header [collapsedHeight]="'40px'" [expandedHeight]="'40px'">
                    <mat-panel-title>
                      <div class="flex flex-1 items-center gap-2">
                        <code class="flex-1 truncate font-mono text-xs text-neutral-700">{{ anchor.id }}</code>
                        @if (getAnchorHighlight(anchor.id); as hl) {
                          <span
                            class="inline-block h-3 w-3 shrink-0 rounded-full"
                            [style.background-color]="colorHexMap[hl.color ?? 'red']"
                          ></span>
                        }
                        @if (getAnchorAnnotation(anchor.id)) {
                          <span class="shrink-0 rounded bg-amber-100 px-1 py-px text-[9px] font-semibold text-amber-600"
                            >ANN</span
                          >
                        }
                      </div>
                    </mat-panel-title>
                  </mat-expansion-panel-header>

                  <ng-template matExpansionPanelContent>
                    <div class="space-y-3 px-3 pb-3 pt-1">
                      <!-- Highlight Section -->
                      <div>
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400"
                          >Highlight</span
                        >
                        <div class="mt-1.5">
                          <dojo-single-select
                            [formCtrl]="anchorHighlightStyleCtrl"
                            [options]="highlightStyleOptions"
                            [optionValue]="'value'"
                            [optionLabel]="'label'"
                            [compact]="true"
                            [size]="'m'"
                            [label]="'Style'"
                            (selectionChange)="updateAnchorHighlight(anchor.id, $event)"
                          />

                          @if (getAnchorHighlight(anchor.id)) {
                            <div class="mt-2.5 flex flex-wrap gap-2.5">
                              @for (color of colorOptions; track color) {
                                <button
                                  class="h-6 w-6 rounded-full transition-transform hover:scale-110"
                                  [style.background-color]="colorHexMap[color]"
                                  [style.box-shadow]="
                                    getAnchorHighlight(anchor.id)?.color === color
                                      ? '0 0 0 2px white, 0 0 0 3.5px ' + colorHexMap[color]
                                      : 'none'
                                  "
                                  [class.scale-110]="getAnchorHighlight(anchor.id)?.color === color"
                                  (click)="updateAnchorHighlightColor(anchor.id, color)"
                                  [title]="color"
                                ></button>
                              }
                            </div>
                          }
                        </div>
                      </div>

                      <!-- Annotation Section -->
                      <div class="rounded-lg border border-neutral-200 bg-neutral-50/50 p-3">
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400"
                          >Annotation</span
                        >
                        <div class="mt-2 space-y-2.5">
                          <dojo-textarea-input
                            [formCtrl]="anchorAnnotationCtrl"
                            [placeholder]="'Hover text (LaTeX)...'"
                            [compact]="true"
                            [size]="'m'"
                            [autoResize]="true"
                            [autoResizeMinRows]="2"
                            [autoResizeMaxRows]="5"
                          />

                          @if (anchorAnnotationCtrl.value) {
                            <div class="annotation-preview rounded border border-neutral-100 bg-white px-2.5 py-2">
                              <markdown
                                [data]="'$' + anchorAnnotationCtrl.value + '$'"
                                katex
                                [katexOptions]="katexOptions"
                              />
                            </div>
                          }

                          <div class="grid grid-cols-2 gap-2">
                            <dojo-single-select
                              [formCtrl]="anchorTriggerCtrl"
                              [options]="triggerOptions"
                              [optionValue]="'value'"
                              [optionLabel]="'label'"
                              [compact]="true"
                              [size]="'m'"
                              [label]="'Trigger'"
                              (selectionChange)="updateAnchorAnnotation(anchor.id, { trigger: $event })"
                            />
                            <dojo-single-select
                              [formCtrl]="anchorPlacementCtrl"
                              [options]="placementOptions"
                              [optionValue]="'value'"
                              [optionLabel]="'label'"
                              [compact]="true"
                              [size]="'m'"
                              [label]="'Placement'"
                              (selectionChange)="updateAnchorAnnotation(anchor.id, { placement: $event })"
                            />
                          </div>

                          @if (getAnchorAnnotation(anchor.id)) {
                            <dojo-button
                              [size]="'s'"
                              [fill]="'clear'"
                              [customColor]="'red'"
                              (click)="removeAnchorAnnotation(anchor.id)"
                            >
                              Remove Annotation
                            </dojo-button>
                          }
                        </div>
                      </div>
                    </div>
                  </ng-template>
                </mat-expansion-panel>
              }
            </mat-accordion>
          }
        </div>

        <!-- Connections List -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-600">
            Connections
            <span class="normal-case tracking-normal">({{ connections().length }})</span>
          </span>

          @if (connections().length === 0) {
            <p class="py-3 text-center text-xs text-neutral-400">No connections yet</p>
          } @else {
            <mat-accordion class="anchor-accordion mt-2" displayMode="flat" multi>
              @for (conn of connections(); track conn.id) {
                <mat-expansion-panel
                  class="anchor-panel"
                  (opened)="onConnectionPanelOpened(conn)"
                  (closed)="onConnectionPanelClosed(conn)"
                >
                  <mat-expansion-panel-header [collapsedHeight]="'40px'" [expandedHeight]="'40px'">
                    <mat-panel-title>
                      <div class="flex flex-1 items-center gap-2">
                        <code class="font-mono text-xs text-neutral-700">{{ conn.from_anchor }}</code>
                        <dojo-icon icon="arrow-right" [size]="10" class="text-neutral-300" />
                        <code class="flex-1 font-mono text-xs text-neutral-700">{{ conn.to_anchor }}</code>
                        @if (conn.style?.color; as connColor) {
                          <span
                            class="inline-block h-3 w-3 shrink-0 rounded-full"
                            [style.background-color]="getColorHex(connColor)"
                          ></span>
                        }
                      </div>
                    </mat-panel-title>
                  </mat-expansion-panel-header>

                  <ng-template matExpansionPanelContent>
                    <div class="space-y-2 px-3 pb-3 pt-1">
                      <!-- Color swatches -->
                      <div>
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Color</span>
                        <div class="mt-1.5 flex flex-wrap gap-2.5">
                          @for (color of colorOptions; track color) {
                            <button
                              class="h-6 w-6 rounded-full transition-transform hover:scale-110"
                              [style.background-color]="colorHexMap[color]"
                              [style.box-shadow]="
                                editingConnection()?.style?.color === color
                                  ? '0 0 0 2px white, 0 0 0 3.5px ' + colorHexMap[color]
                                  : 'none'
                              "
                              [class.scale-110]="editingConnection()?.style?.color === color"
                              (click)="updateConnectionStyle('color', color)"
                              [title]="color"
                            ></button>
                          }
                        </div>
                      </div>

                      <dojo-number-input
                        [formCtrl]="connWidthCtrl"
                        [label]="'Line Width'"
                        [compact]="true"
                        [size]="'m'"
                        [min]="0.5"
                        [max]="5"
                        [step]="0.1"
                      />

                      <dojo-single-select
                        [formCtrl]="connLineStyleCtrl"
                        [options]="lineStyleSelectOptions"
                        [optionValue]="'value'"
                        [optionLabel]="'label'"
                        [compact]="true"
                        [size]="'m'"
                        [label]="'Line Style'"
                        (selectionChange)="updateConnectionStyle('line_style', $event)"
                      />

                      <dojo-single-select
                        [formCtrl]="connArrowStyleCtrl"
                        [options]="arrowStyleOptions"
                        [optionValue]="'value'"
                        [optionLabel]="'label'"
                        [compact]="true"
                        [size]="'m'"
                        [label]="'Arrow Style'"
                        (selectionChange)="updateConnectionStyle('arrow_style', $event)"
                      />

                      <div class="grid grid-cols-2 gap-2">
                        <dojo-single-select
                          [formCtrl]="connFromSocketCtrl"
                          [options]="socketOptions"
                          [optionValue]="'value'"
                          [optionLabel]="'label'"
                          [compact]="true"
                          [size]="'m'"
                          [label]="'From'"
                          (selectionChange)="updateConnectionStyle('from_socket', $any($event))"
                        />
                        <dojo-single-select
                          [formCtrl]="connToSocketCtrl"
                          [options]="socketOptions"
                          [optionValue]="'value'"
                          [optionLabel]="'label'"
                          [compact]="true"
                          [size]="'m'"
                          [label]="'To'"
                          (selectionChange)="updateConnectionStyle('to_socket', $any($event))"
                        />
                      </div>

                      <dojo-number-input
                        [formCtrl]="connStubCtrl"
                        [label]="'Stub Length'"
                        [compact]="true"
                        [size]="'m'"
                        [min]="0"
                        [max]="50"
                      />

                      <!-- Label Section -->
                      <div class="rounded-lg border border-neutral-200 bg-neutral-50/50 p-3">
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Label</span>
                        <div class="mt-2 space-y-2">
                          <dojo-text-input
                            [formCtrl]="connLabelTextCtrl"
                            [label]="'Text'"
                            [placeholder]="'Label text (LaTeX)'"
                            [compact]="true"
                            [size]="'m'"
                          />

                          @if (connLabelTextCtrl.value) {
                            <dojo-single-select
                              [formCtrl]="connLabelStyleCtrl"
                              [options]="labelStyleOptions"
                              [optionValue]="'value'"
                              [optionLabel]="'label'"
                              [compact]="true"
                              [size]="'m'"
                              [label]="'Label Style'"
                              (selectionChange)="updateConnectionLabel('style', $event)"
                            />

                            <dojo-number-input
                              [formCtrl]="connLabelLocationCtrl"
                              [label]="'Location (0-1)'"
                              [compact]="true"
                              [size]="'m'"
                              [min]="0"
                              [max]="1"
                              [step]="0.1"
                            />

                            <div class="grid grid-cols-2 gap-2">
                              <dojo-number-input
                                [formCtrl]="connLabelOffsetXCtrl"
                                [label]="'Offset X'"
                                [compact]="true"
                                [size]="'m'"
                              />
                              <dojo-number-input
                                [formCtrl]="connLabelOffsetYCtrl"
                                [label]="'Offset Y'"
                                [compact]="true"
                                [size]="'m'"
                              />
                            </div>

                            <dojo-button
                              [size]="'s'"
                              [fill]="'clear'"
                              [customColor]="'red'"
                              (click)="removeConnectionLabel()"
                            >
                              Remove Label
                            </dojo-button>
                          }
                        </div>
                      </div>

                      <!-- Delete -->
                      <dojo-button
                        [size]="'s'"
                        [fill]="'clear'"
                        [customColor]="'red'"
                        (click)="deleteConnection(conn.id)"
                      >
                        Delete Connection
                      </dojo-button>
                    </div>
                  </ng-template>
                </mat-expansion-panel>
              }
            </mat-accordion>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Footer with Cancel / Save -->
  <div class="flex shrink-0 items-center justify-end gap-3 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
    <dojo-button [size]="'m'" [fill]="'outline'" (click)="editorClose.emit()">Cancel</dojo-button>
    <dojo-button [size]="'m'" [fill]="'solid'" [disabled]="!isDirty()" (click)="save()">Save</dojo-button>
  </div>
</div>

<!-- Anchor Creation Dialog (rendered as overlay within modal) -->
@if (showAnchorDialog()) {
  <div
    class="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40"
    (click)="closeAnchorDialog()"
    (mousedown)="$event.stopPropagation()"
    (mousemove)="$event.stopPropagation()"
    (mouseup)="$event.stopPropagation()"
  >
    <div
      class="w-80 rounded-lg border border-neutral-200 bg-white p-5 shadow-xl"
      (click)="$event.stopPropagation()"
      (mousedown)="$event.stopPropagation()"
    >
      <h3 class="mb-1 text-sm font-semibold text-neutral-900">Create Anchor</h3>
      <p class="mb-3 text-xs text-neutral-500">
        For: <strong class="text-neutral-700">"{{ pendingAnchor()?.text }}"</strong>
      </p>
      <div class="mb-3">
        <label class="mb-1 block text-[11px] font-medium text-neutral-500">Anchor ID</label>
        <input
          class="anchor-id-input w-full rounded border border-neutral-200 px-2.5 py-1.5 font-mono text-sm text-neutral-700 outline-none transition-colors focus:border-blue-400"
          [value]="newAnchorId()"
          (input)="newAnchorId.set($any($event.target).value)"
          placeholder="e.g., term-x, equation-1"
          (keydown.enter)="confirmCreateAnchor()"
        />
      </div>
      <div class="flex gap-2">
        <dojo-button [size]="'m'" [fill]="'solid'" (click)="confirmCreateAnchor()" class="flex-1">Create</dojo-button>
        <dojo-button [size]="'m'" [fill]="'outline'" (click)="closeAnchorDialog()">Cancel</dojo-button>
      </div>
    </div>
  </div>
}
